'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Treatment } from '@/lib/treatments-data';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Shield, Award, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
    treatment: Treatment;
}


export default function VeneersHeroSection({ treatment }: HeroSectionProps) {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showAfter, setShowAfter] = useState(false);

    const beforeAfterImages = [
        {
            before: '/images/treatments/dental-veneers/gallery-1-before.webp',
            after: '/images/treatments/dental-veneers/gallery-1-after.webp',
            description: t('hero.beforeAfter.descriptions.transformation')
        },
        {
            before: '/images/treatments/dental-veneers/gallery-5-before.webp',
            after: '/images/treatments/dental-veneers/gallery-5-after.webp',
            description: t('hero.beforeAfter.descriptions.porcelain')
        }
    ];
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states for all animated elements
            gsap.set([titleRef.current, subtitleRef.current, ctaRef.current, imageRef.current], {
                opacity: 0,
                y: 60,
                scale: 0.95
            });

            // Hero title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1.2,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Subtitle animation
            gsap.to(subtitleRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: subtitleRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // CTA animation
            gsap.to(ctaRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: ctaRef.current,
                    start: "top 70%",
                    end: "bottom 30%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Image container animation
            gsap.to(imageRef.current, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 1.5,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: imageRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Auto-cycle through images
    const imagesLength = beforeAfterImages.length;
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % imagesLength);
        }, 5000);

        return () => clearInterval(interval);
    }, [imagesLength]);

    // Auto-toggle before/after
    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setShowAfter(prev => !prev);
        }, 3000);

        return () => clearInterval(toggleInterval);
    }, []);

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + beforeAfterImages.length) % beforeAfterImages.length);
    };

    const handleConsultationClick = () => {
        const gallerySection = document.getElementById('gallery');
        gallerySection?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleExploreClick = () => {
        const typesSection = document.getElementById('types');
        typesSection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden"
        >
            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold opacity-5 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative py-10 lg:py-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                    {/* Left Content */}
                    <div className={cn(
                        "space-y-8 text-center",
                        isRTL ? "lg:text-right" : "lg:text-left"
                    )}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className={cn(
                                "inline-flex items-center bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium gap-2",
                                isRTL && "flex-row-reverse font-arabic"
                            )}
                        >
                            <Sparkles className="w-4 h-4" />
                            <span>{t('hero.badge')}</span>
                        </motion.div>

                        <h1
                            ref={titleRef}
                            className={cn(
                                "text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-serif",
                                isRTL && "font-arabic"
                            )}
                        >
                            {(() => {
                                const locationBadge = (
                                    <span className="text-gold relative">
                                        {t('hero.location')}
                                        <motion.div
                                            className="absolute -inset-2 bg-gold/20 rounded-lg"
                                            animate={{
                                                boxShadow: [
                                                    '0 0 20px rgb(var(--gold-rgb) / 0.3)',
                                                    '0 0 30px rgb(var(--gold-rgb) / 0.5)',
                                                    '0 0 20px rgb(var(--gold-rgb) / 0.3)'
                                                ]
                                            }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                        />
                                    </span>
                                );
                                const isLocationBefore = t('hero.locationPosition') === 'before';
                                return (
                                    <>
                                        {isLocationBefore && <>{locationBadge}{' '}</>}
                                        {t('hero.title')}{' '}
                                        {!isLocationBefore && locationBadge}
                                    </>
                                );
                            })()}
                        </h1>

                        <p
                            ref={subtitleRef}
                            className={cn(
                                "text-xl md:text-2xl text-slate-300 leading-relaxed max-w-2xl mx-auto",
                                isRTL ? "lg:mx-0 font-arabic" : "lg:mx-0"
                            )}
                        >
                            {t('hero.subtitle')}
                        </p>

                        <div ref={ctaRef} className="space-y-6">
                            <div className={cn(
                                "flex flex-col sm:flex-row gap-4 justify-center",
                                isRTL ? "lg:justify-end" : "lg:justify-start"
                            )}>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Button
                                        onClick={handleConsultationClick}
                                        size="lg"
                                        className={cn(
                                            "bg-gold hover:bg-gold-600 text-navy px-5 py-3 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-gold hover:border-gold-600 inline-flex items-center justify-center gap-2",
                                            isRTL && "font-arabic"
                                        )}
                                    >
                                        <span>{t('hero.buttons.bookConsultation')}</span>
                                        <ArrowRight className={cn(
                                            "w-4 h-4 sm:w-5 sm:h-5 shrink-0",
                                            isRTL && "rotate-180"
                                        )} />
                                    </Button>
                                </motion.div>

                                <button
                                    onClick={handleExploreClick}
                                    className="text-gold hover:text-gold-600 transition-colors duration-300 font-medium text-lg border-b-2 border-gold hover:border-gold-600 pb-1"
                                >
                                    {t('hero.buttons.exploreOptions')}
                                </button>
                            </div>

                            {/* Trust indicators */}
                            <div className={cn(
                                "flex flex-wrap justify-center items-center gap-6 text-sm text-slate-300",
                                isRTL ? "lg:justify-end" : "lg:justify-start"
                            )}>
                                <motion.div
                                    className={cn(
                                        "flex items-center gap-2",
                                        isRTL && "flex-row-reverse"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Star className="w-5 h-5 text-gold fill-current" />
                                    <span>{t('hero.trustIndicators.rating')}</span>
                                </motion.div>
                                <motion.div
                                    className={cn(
                                        "flex items-center gap-2",
                                        isRTL && "flex-row-reverse"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Shield className="w-5 h-5 text-gold" />
                                    <span>{t('hero.trustIndicators.lifespan')}</span>
                                </motion.div>
                                <motion.div
                                    className={cn(
                                        "flex items-center gap-2",
                                        isRTL && "flex-row-reverse"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Award className="w-5 h-5 text-gold" />
                                    <span>{t('hero.trustIndicators.craftsmanship')}</span>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Right Image - Before/After Slider */}
                    <div className="relative flex justify-center lg:justify-end">
                        <div ref={imageRef} className="relative z-10">
                            <div className="w-80 h-80 md:w-96 md:h-96 relative">
                                {/* Before/After Image Container */}
                                <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl border-4 border-gold/30">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={`${currentImageIndex}-${showAfter}`}
                                            initial={{ opacity: 0, scale: 1.1 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            transition={{ duration: 0.8 }}
                                            className="relative w-full h-full"
                                        >
                                            <OptimizedImage
                                                src={showAfter ? beforeAfterImages[currentImageIndex].after : beforeAfterImages[currentImageIndex].before}
                                                alt={showAfter ? t('hero.altTexts.afterVeneers') : t('hero.altTexts.beforeVeneers')}
                                                width={400}
                                                height={400}
                                                className="w-full h-full object-cover"
                                            />

                                            {/* Before/After Label */}
                                            <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                {showAfter ? t('hero.beforeAfter.after') : t('hero.beforeAfter.before')}
                                            </div>

                                            {/* Gradient overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-navy/20 via-transparent to-gold/10"></div>
                                        </motion.div>
                                    </AnimatePresence>

                                    {/* Navigation arrows */}
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300"
                                    >
                                        <ChevronLeft className="w-6 h-6" />
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300"
                                    >
                                        <ChevronRight className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Floating elements — smaller + tucked in on
                                    mobile so they don't overflow. */}
                                <motion.div
                                    className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-full flex items-center justify-center shadow-lg"
                                    animate={{
                                        y: [0, -10, 0],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <span className="text-xl sm:text-2xl">✨</span>
                                </motion.div>
                                <motion.div
                                    className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center shadow-lg"
                                    animate={{
                                        y: [0, 8, 0],
                                        rotate: [0, -5, 5, 0]
                                    }}
                                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                >
                                    <span className="text-lg sm:text-xl">😊</span>
                                </motion.div>

                                {/* Image indicator dots */}
                                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
                                    {beforeAfterImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentImageIndex
                                                ? 'bg-gold scale-125'
                                                : 'bg-white/50 hover:bg-white/70'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <motion.div
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-slate-300"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                    <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm">{t('hero.bottomText')}</span>
                        <div className="w-px h-8 bg-gold"></div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
